import { ActivityIcon } from '@jouzen/ecom-components/icons/ActivityIcon';
import { HeartOrganIcon } from '@jouzen/ecom-components/icons/HeartOrganIcon';
import { MeditationIcon } from '@jouzen/ecom-components/icons/MeditationIcon';
import { MetabolicHealthWaveIcon } from '@jouzen/ecom-components/icons/MetabolicHealthWaveIcon';
import { MoonIcon } from '@jouzen/ecom-components/icons/MoonIcon';
import { StressIcon } from '@jouzen/ecom-components/icons/StressIcon';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { List } from '@jouzen/ecom-components/v3/List';
import { ListItem } from '@jouzen/ecom-components/v3/ListItem';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import {
  Carousel,
  CarouselSlides,
  CarouselTransitionType,
} from '@jouzen/ecom-components/v4/Carousel';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

interface CarouselImage {
  id: number;
  alt: MessageKey;
  src: string;
}

interface FeatureListItem {
  id: number;
  label: MessageKey;
  Icon: SVGIconComponent;
}

const carouselImages: CarouselImage[] = [
  {
    id: 1,
    alt: 'membership_redesign_health_features_image_1_alt',
    src: 'membership_redesign_health_features_image_1',
  },
  {
    id: 2,
    alt: 'man_exercise_alt',
    src: 'membership_redesign_health_features_image_2',
  },
  {
    id: 3,
    alt: 'pop_membership_sleep_featured_or4_img_alt',
    src: 'membership_redesign_health_features_image_3',
  },
  {
    id: 4,
    alt: 'membership_redesign_health_features_image_4_alt',
    src: 'membership_redesign_health_features_image_4',
  },
];

const featureListItems: FeatureListItem[] = [
  { id: 1, label: 'sleep_rest_tag', Icon: MoonIcon },
  { id: 2, label: 'heart_health', Icon: HeartOrganIcon },
  { id: 3, label: 'activity_and_movement_link', Icon: ActivityIcon },
  { id: 4, label: 'stress_tag', Icon: StressIcon },
  { id: 5, label: 'womens_health', Icon: MeditationIcon },
  { id: 6, label: 'metabolic_health', Icon: MetabolicHealthWaveIcon },
];

const HealthFeaturesSection = () => {
  return (
    <section className="py-10 lg:py-20" data-cy="health-features-section">
      <LayoutGrid className="gap-y-6 lg:gap-y-20">
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <Typography
            variant="h1"
            Element="h2"
            align="center"
            font="serif"
            className="leading-tighter tracking-tighter"
          >
            <RichText messageKey="membership_redesign_health_features_title" />
          </Typography>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'main', lg: 13 }}
          colStart={{ sm: 'main', lg: 3 }}
          aria-hidden="true"
          className="flex items-center"
        >
          <Carousel
            autoplay
            infinite
            interval={3}
            transitionDuration={1}
            transitionType={CarouselTransitionType.Fade}
            className="aspect-square w-full lg:aspect-[600/480]"
          >
            <CarouselSlides>
              {carouselImages.map((image) => (
                <div key={image.id} className="relative">
                  <Image
                    alt={image.alt}
                    src={image.src}
                    fill
                    data-cy="health-features-carousel-image"
                    sizes="(max-width: 1023px) 100vw, 50vw"
                    className="rounded-3xl object-cover"
                  />
                </div>
              ))}
            </CarouselSlides>
          </Carousel>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'main', lg: 23 }}
          colStart={{ sm: 'main', lg: 14 }}
          className="flex flex-col justify-center"
        >
          <Typography className="mb-10" data-cy="health-features-copy">
            <RichText messageKey="membership_redesign_health_features_copy" />
          </Typography>
          <List
            className="grid grid-cols-1 gap-x-4 gap-y-2 lg:grid-cols-2 lg:gap-y-6"
            unstyled
          >
            {featureListItems.map(({ id, label, Icon }) => (
              <ListItem
                key={id}
                className="flex items-center rounded-[176px] border border-sandstone-500 px-6 py-4 lg:px-4"
                data-cy="health-features-list-item"
              >
                <Icon width={24} height={24} />
                <Typography className="ml-4 text-sm lg:text-base" weight="bold">
                  <RichText messageKey={label} />
                </Typography>
              </ListItem>
            ))}
          </List>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default HealthFeaturesSection;
